/**
 * Dutch, the source catalogue: it defines the keys, every other catalogue has the same ones (see `Catalogue`).
 *
 * - The user is addressed informally: "je" (not "u"), and so "du" in German and "tu" in French.
 * - `{name}` is a placeholder, filled in by the terminal; keep it as is (it may move within the sentence).
 * - A message with plural forms (`one`, `other`, ...) is picked by the `{count}` it is given. `other` is required, the
 *   other categories are those of `Intl.PluralRules` for the language (e.g. `many` in French).
 * - "Manco" is the warehouse term for an order that has a problem, with the order or its products (missing stock, a
 *   wrong address, ...), that stops it from being processed any further until the problem is resolved.
 * - "Shipping Terminal", "PostNL Cargo", "development" and "production" are names, they stay as they are.
 */

/** A message with a form per plural category, see `Intl.PluralRules`. */
export type PluralMessage = {other: string} & Partial<Record<Intl.LDMLPluralRule, string>>;

export const nl = {
    // shared by the modals and pages
    // closes a dialog
    'common.close': 'Sluiten',
    'common.cancel': 'Annuleren',
    'common.error': 'Fout',
    'common.retry': 'Opnieuw proberen',
    // the aria-label of the loading animation
    'common.loading': 'Laden...',
    // an error from the API without any message
    'common.unknownError': 'Onbekende fout',
    // in a dialog while its request is underway: keys, scans and closing wait for it
    'common.busy': 'Bezig… wacht op het resultaat.',
    // after the label of an optional field
    'common.optional': '(optioneel)',

    // the page for an error while rendering, and the page for an unknown URL: what happened and what to do next
    'errorPage.heading': 'Deze pagina kon niet worden getoond',
    'errorPage.message':
        'Er ging iets fout in de terminal en de fout is doorgegeven aan de ontwikkelaars. Probeer het opnieuw, of ga naar home als het blijft gebeuren.',
    // a page's data could not be fetched from the API; the API's message is under it, with a button to try again
    'errorPage.loadFailed': 'De gegevens konden niet worden opgehaald',
    // goes back to the previous page
    'errorPage.back': 'Terug',
    // to the start page (the terminal, or the login)
    'errorPage.home': 'Home',
    'notFound.heading': 'Deze pagina bestaat niet',
    'notFound.message': 'Controleer het adres, of ga naar home om verder te werken.',

    // the top bar
    'topNav.changeStation': 'Wissel van station',
    'topNav.logout': 'Uitloggen',
    // the user menu behind the avatar: "Gebruikersmenu van Anouk de Vries"
    'topNav.userMenu': 'Gebruikersmenu van {name}',
    // the user's language menu: a language picked on this terminal, or the one of their account; the language is named
    // in its own words ("Nederlands", "Français")
    'topNav.language': 'Taal',
    'topNav.languageButton': 'Taal: {language}',
    'topNav.accountLanguage': 'Van mijn account ({language})',
    // under the language menu, after the keys: "↑ ↓ kiezen, Enter bevestigen, F7 Esc sluiten"
    'topNav.menuArrowUp': 'Pijl omhoog',
    'topNav.menuArrowDown': 'Pijl omlaag',
    'topNav.menuPick': 'kiezen',
    'topNav.menuConfirm': 'bevestigen',
    'topNav.menuClose': 'sluiten',
    // the theme button (F8) switches to the other theme and is named after it
    'topNav.lightTheme': 'Licht thema',
    'topNav.darkTheme': 'Donker thema',
    // the environment button: "Omgeving production, wissel naar development"
    'topNav.environment': 'Omgeving {environment}, wissel naar {other}',

    // a new version of the terminal was deployed: "Nieuwe versie beschikbaar — wordt automatisch herladen over 5 min"
    'update.available': 'Nieuwe versie beschikbaar',
    'update.reloadNow': 'Nu herladen',
    // the reload is due, but waits until the operator is done with the order on screen
    'update.reloadWhenIdle': 'wordt herladen zodra de terminal vrij is',
    // time is `update.seconds` or `update.minutes`
    'update.reloadIn': 'wordt automatisch herladen over {time}',
    'update.seconds': {one: '{count} s', other: '{count} s'},
    'update.minutes': {one: '{count} min', other: '{count} min'},

    // switching the terminal to the test API (development)
    'environment.title': 'Let op',
    'environment.switch': 'Switch naar development',
    'environment.cancel': 'Annuleer',
    'environment.warning':
        'Shipping Terminal zal verbonden worden met de test omgeving en geeft geen productie data. Deze omgeving is alleen bedoeld voor het testen van nieuwe features.',

    // the login page: pick a user, then enter the PIN
    'login.title': 'Wie logt er in?',
    'login.loadingUsers': 'Gebruikers ophalen...',
    'pin.verifying': 'Pincode verifiëren...',
    // over the PIN boxes; the second one after the idle lock (5 minutes without activity)
    'pin.prompt': 'Voer je pincode in',
    'pin.promptIdle': 'Inactief: voer je pincode opnieuw in',
    // the name of a PIN box for screen readers: index is 1 to total (5)
    'pin.digit': 'Cijfer {index} van {total}',
    // logs out, back to the user list
    'pin.changeUser': 'Wijzig gebruiker',
    // a wrong PIN, or the API's rate limit
    'pin.errorInvalid': 'Pincode onjuist, of te veel pogingen. Probeer het opnieuw.',
    // message is a technical error message (in English)
    'pin.errorFailed': 'Er ging iets fout bij het verifiëren van de pincode: {message}',

    // the terminal page, before an order is scanned: a station is a box label printer
    'terminal.loadingPrinters': 'Beschikbare printers ophalen...',
    'terminal.noPrinters': 'Geen printers gevonden',
    'terminal.selectStation': 'Selecteer station',
    // under the stations: each has a number key
    'terminal.selectStationHint': 'Klik op de printer van dit station, of druk op zijn cijfer',
    // the band at the top, before an order is scanned (with an order it shows the order's status)
    'terminal.status.idle': 'Klaar om te scannen',
    // on the band when the status of the order does not change by itself: the connection is lost, or was meanwhile
    'terminal.status.notLive': 'Status niet live',
    'terminal.status.mayBeOutdated': 'Status mogelijk niet actueel',

    // the action bar below the order (each button has a function key)
    'terminal.actions': 'Acties',
    'terminal.button.printLabel': 'Print extra label',
    'terminal.button.manco': 'Manco',
    // all shipments of the order: they are voided at the carrier, the stock is booked back, and the order is paid (not
    // shipped) again
    'terminal.button.deleteShipment': 'Verwijder zendingen',
    // opens the order in the polyestershoppen.nl admin
    'terminal.button.openInAdmin': 'Open in admin',

    // the label of the order number input, which shows the number of the order on screen
    'order.number': 'Order',
    'order.numberPlaceholder': 'Ordernummer',
    // the button next to the order number typed by hand
    'order.scan': 'Scannen',
    // user is the name of the warehouse employee who picked the order
    'order.pickedBy': 'Gepickt door {user}',
    'order.pickedByUnknown': 'onbekend',
    // date is the order's creation date and time
    'order.created': 'Aangemaakt {date}',
    'order.scanPrompt': 'Scan een order',
    'order.scanHint': 'Of typ het nummer en druk op Enter',
    // a scan the terminal cannot use (a toast): code is what came in; F2 jumps to the order number input
    'scan.notRecognized': 'Scan niet herkend',
    'scan.notAnOrder': '“{code}” is geen ordernummer of containercode. Scan opnieuw, of typ het ordernummer (F2).',
    'scan.tooSlow':
        '“{code}” kwam te langzaam binnen voor een scan. Scan opnieuw, of typ het ordernummer in het invoerveld (F2).',
    // under the order number input, for what was typed there
    'scan.invalidInput': 'Typ een ordernummer van minstens 6 cijfers, of een containercode die met een B begint.',
    // the order number or container code scanned could not be loaded; the API's reason follows
    'scan.failed': '“{code}” kon niet geladen worden',
    // scanned a container (its code is in `scan.failed` above it) that has no order to ship
    'scan.noOrderInContainer': 'Er zit geen open order in deze container',
    // the title above the products to pack
    'order.products': 'Inpakken',
    // the shipping method, next to the products
    'order.shipping': 'Verzending',
    'order.noShipping': 'Geen verzendmethode',
    // the order's warehouse note ("Notitie magazijn" in the admin): next to the products, and in a dialog after the
    // scan that has to be dismissed before the labels print
    'order.warehouseNote': 'Notitie magazijn',
    'order.noWarehouseNote': 'Geen notitie magazijn',
    // the button that dismisses the dialog with the warehouse note: the packer has read it
    'order.warehouseNoteRead': 'Gelezen',
    // the warnings above the products, only when there are any
    'order.warnings': 'Let op bij het inpakken',
    'order.warning.hazard': 'Waarschuwingslabels plakken',
    'order.warning.cans': 'Er zitten blikken in deze order: denk om het inpakken',
    'order.warning.cargo': 'Verzenden als vracht: kies het palletformaat bij het printen',
    'order.hazardImageAlt': 'Waarschuwingslabel',

    // the totals below the products
    'meta.items': 'Artikelen',
    'meta.weight': 'Gewicht',
    // the largest length, width and height of the products (of any product each)
    'meta.largestSize': 'Grootste maat (l × b × h)',
    // value is a size in centimeters, or the three of the largest size
    'meta.centimeters': '{value} cm',
    // a size that is unknown: "not applicable"
    'meta.notApplicable': 'nvt',

    // the delivery address next to the products
    'address.title': 'Bezorgadres',

    // the carrier refused the address: the operator corrects it and creates the label again
    'addressError.title': 'Adres corrigeren',
    // under the carrier's reason the address was refused
    'addressError.hint': 'Pas het adres aan en maak het label opnieuw aan.',
    'addressError.createLabel': 'Label aanmaken',
    // searches the address on Google Maps
    'addressError.searchGoogle': 'Zoek adres op Google',
    'addressError.firstName': 'Voornaam',
    'addressError.lastName': 'Achternaam',
    'addressError.company': 'Bedrijfsnaam',
    'addressError.street': 'Straat',
    'addressError.houseNumber': 'Huisnummer',
    // the house number addition, a narrow field
    'addressError.addition': 'Toevoeging',
    'addressError.zipCode': 'Postcode',
    'addressError.city': 'Woonplaats',
    'addressError.email': 'E-mail',
    'addressError.phone': 'Telefoonnummer',
    'addressError.country': 'Land',

    // the countries an address can be corrected to
    'country.NL': 'Nederland',
    'country.BE': 'België',
    'country.FR': 'Frankrijk',
    'country.IT': 'Italië',
    'country.DE': 'Duitsland',

    // reporting that an order cannot be shipped (digits 1 to 3 pick the reason)
    'manco.title': 'Manco melden',
    'manco.address': 'Adres gegevens kloppen niet',
    // customs
    'manco.customs': 'Internationale zending error',
    'manco.other': 'Overig',
    'manco.reported': 'Manco succesvol gemeld',
    // message is the error from the API
    'manco.failed': 'Manco melden mislukt: {message}',

    // shown with an animation for orders with cans in them
    'packing.title': 'Let op!',
    'packing.cans': 'Denk om het inpakken van blikken',

    // picking the kind of pallet for a cargo shipment (PostNL Cargo, ...); the title is the shipping method's name, this
    // one only for a method without a name
    'cargo.title': 'Vracht',
    'cargo.pallet': 'Pallet',
    'cargo.halfPallet': 'Halve Pallet',
    'cargo.miniPallet': 'Mini Pallet',
    'cargo.blockPallet': 'Blokpallet',
    // packages without a pallet
    'cargo.packages': 'Stukgoed',

    // printing the shipping labels for an order: one per box (collo)
    'printLabel.title': 'Print extra labels',
    // the labels of an order with more than one box, right after it was scanned
    'printLabel.titleAuto': 'Labels printen',
    'printLabel.estimated': 'Geschatte labels nodig: {count}',
    // an amount of labels other than the buttons 1 to 4
    'printLabel.otherAmount': 'Ander aantal:',
    // the name of a button with a digit on it
    'printLabel.count': {one: '{count} label', other: '{count} labels'},
    // prints the amount typed in "Ander aantal"
    'printLabel.print': 'Printen',

    // hazard labels (e.g. flammable) that have to go on the box
    'warningLabels.title': 'Let op!',
    'warningLabels.message': 'Deze order heeft waarschuwingslabels!',
    'warningLabels.imageAlt': 'Warning Label',

    // the question before F10 removes the shipments; id is the order's number
    'removeShipments.title': 'Zendingen verwijderen?',
    'removeShipments.text':
        'Alle zendingen van order {id} worden verwijderd en bij de vervoerder geannuleerd. De voorraad wordt teruggeboekt en de order staat daarna weer op betaald.',

    // notifications after printing or removing the shipments; the header of a warning from the API about the order
    'shipping.printWarning': 'Let op',
    'shipping.shipmentRemoved': 'Zendingen verwijderd',
    // a warning after printing: a manco was reported for the order and it was not picked again since
    'shipping.warningMancoReported': 'Deze order is manco gemeld en mogelijk niet volledig gepickt',
    // above the API's reason why the order (id) could not be shipped; it need not be the order on screen any more
    'shipping.orderFailed': 'Order {id} is niet verzonden',
    // a scan or a key while the label of the order (id) that was scanned before still prints
    'shipping.stillPrinting': 'Order {id} wordt nog geprint: scan opnieuw zodra dat klaar is',

    // in the F10 dialog: the API refused it (message is its error, nothing was removed); F10 again after an unknown outcome
    'removeShipments.failed': 'De zendingen van order {id} zijn niet verwijderd: {message}',
    'removeShipments.removeAnyway': 'Toch verwijderen',

    // a shipping action without a clear outcome, in its dialog: what happened to the order (id) and what to do now. F11
    // opens the order in the admin, F10 removes its shipments.
    'outcome.unknownPrint': 'Niet bekend of de labels van order {id} geprint zijn',
    'outcome.unknownPrintHint':
        'Er kwam geen duidelijk antwoord van de server. Kijk bij de printer en controleer de order in de admin (F11) voordat je opnieuw print.',
    'outcome.unknownRemove': 'Niet bekend of de zendingen van order {id} verwijderd zijn',
    'outcome.unknownRemoveHint':
        'Er kwam geen duidelijk antwoord van de server. Controleer de order in de admin (F11) voordat je het opnieuw probeert.',
    'outcome.unknownManco': 'Niet bekend of order {id} manco gemeld is',
    'outcome.unknownMancoHint':
        'Er kwam geen duidelijk antwoord van de server. Controleer de order in de admin (F11) voordat je het opnieuw meldt.',
    'outcome.busy': 'Er loopt nog een andere verzendactie voor order {id}',
    'outcome.busyHint':
        'Wacht tot die klaar is en controleer de order dan in de admin (F11) voordat je het opnieuw probeert.',
    // after another shipping action was busy: how long to wait
    'outcome.retryAfter': {
        one: 'Probeer het over {count} seconde opnieuw.',
        other: 'Probeer het over {count} seconden opnieuw.',
    },
    // under a shipping action without a clear outcome: what the server said (message is its text)
    'outcome.reason': 'Melding van de server: {message}',
    'outcome.printFailed': 'De labels van order {id} zijn aangemaakt, maar niet geprint',
    'outcome.printFailedHint':
        'Print niet opnieuw, dan worden ze dubbel aangemaakt. Verwijder eerst de zendingen (F10) en print daarna opnieuw.',
    'outcome.printFailedExtraHint':
        'Print niet opnieuw, dan worden ze dubbel aangemaakt. Controleer de order in de admin (F11).',

    // an error of the API, by its code: what was refused or went wrong. {message} is the API's own text, in English
    // (the carrier's reason, or what is invalid). The carrier is the company that delivers the parcel (PostNL, DHL, ...).
    'apiError.notFound': 'Niet gevonden',
    'apiError.sessionExpired': 'De sessie is verlopen. Log opnieuw in.',
    'apiError.printerNotFound': 'De printer is niet gevonden. Kies het inpakstation opnieuw.',
    'apiError.personalAccountRequired': 'Dit kan alleen met een persoonlijk account, niet met een gedeeld account',
    'apiError.validationFailed': 'Ongeldige gegevens: {message}',
    'apiError.internationalShipment': 'Internationale zending: die verstuurt de klantenservice',
    'apiError.shipsWithParentOrder': 'Dit is een nalevering: die gaat met de hoofdorder mee',
    'apiError.tooEarlyForCarrier': 'Nog te vroeg voor de vervoerder (PostNL vanaf 7:30)',
    'apiError.orderAlreadySent': 'De order is al verzonden',
    'apiError.orderNotPaid': 'De order is niet betaald',
    'apiError.vatNumberNotValidated': 'Het btw-nummer van de klant is nog niet gecontroleerd',
    'apiError.shippingDateInFuture': 'De verzenddatum van de order ligt in de toekomst',
    'apiError.wrongPackingStation': 'Deze order kan niet vanaf dit inpakstation verzonden worden',
    'apiError.paymentNotFound': 'De betaling is niet gevonden bij de betaalprovider',
    'apiError.paymentAuthorizationLost': 'De autorisatie van de betaling is verlopen',
    'apiError.customerInvoiceMissing': 'De factuur van de klant ontbreekt',
    'apiError.cargoShippingMethod': 'Dit is een vrachtzending: kies een palletformaat',
    'apiError.notCargoShippingMethod': 'Dit is geen vrachtzending',
    'apiError.orderHasShipments': 'De order heeft al zendingen waarvan de labels niet geprint zijn',
    'apiError.orderHasNoShipments': 'De order heeft geen zendingen',
    'apiError.recipientNameInvalid': 'De vervoerder weigert de naam',
    'apiError.postalCodeInvalid': 'De vervoerder weigert de postcode',
    'apiError.houseNumberInvalid': 'De vervoerder weigert het huisnummer of de toevoeging',
    'apiError.phoneInvalid': 'De vervoerder weigert het telefoonnummer',
    'apiError.emailInvalid': 'De vervoerder weigert het e-mailadres',
    'apiError.addressInvalid': 'De vervoerder weigert de straat of de woonplaats',
    'apiError.servicePointInvalid': 'Het afhaalpunt is niet geldig',
    'apiError.labelRejected': 'De vervoerder weigert het label: {message}',
    'apiError.adrConfigurationMissing': 'De ADR-gegevens van een product ontbreken',
    'apiError.shipmentFailed': 'De zending is mislukt: {message}',
    'apiError.labelsNotPrinted': 'De labels zijn aangemaakt, maar niet geprint',
    'apiError.orderBusy': 'Er loopt nog een andere actie voor deze order',
    'apiError.paymentProviderUnavailable': 'De betaalprovider is niet bereikbaar. Probeer het zo opnieuw.',
} satisfies Record<string, string | PluralMessage>;

/** The shape of every catalogue: the keys of the Dutch one, with a plain or a plural message as there. */
export type Catalogue = {[Key in keyof typeof nl]: (typeof nl)[Key] extends string ? string : PluralMessage};
